import * as angular from "angular";
import { createLabelDescriptionFrom } from "@jsonforms/core";
import {
	controlPath,
	elementState,
	enumOptions,
	errorsAtPath,
	dataAtPath,
	JsonSchemaDefinition,
	FormUiSchema,
	schemaAtScope,
	schemaType
} from "../runtime";
import type { FormSession } from "./form-session";
import type { ControlState } from "./control-state";
import arrayControl from "./array-control";
import alternativeControl from "./alternative-control";
import booleanControl from "./boolean-control";
import dateControl from "./date-control";
import datetimeControl from "./datetime-control";
import enumControl from "./enum-control";
import fileControl from "./file-control";
import layout from "./layout";
import label from "./label";
import markdown from "./markdown";
import numberControl from "./number-control";
import nullControl from "./null-control";
import objectControl from "./object-control";
import reference from "./reference";
import stringControl from "./string-control";
import template from "./node.html";

export interface NodeController {
	session: FormSession;
	ui: FormUiSchema;
	context: JsonSchemaDefinition;
	basePath: string;
	parentEnabled: boolean;
	revision: number;
}

// AngularJS assigns bindings before constructing the controller.
// eslint-disable-next-line no-redeclare
export class NodeController implements angular.IController {
	public static $inject = ["$scope"];

	public readonly inputId: string;

	public visible = true;

	public enabled = true;

	public path = "";

	public schema: JsonSchemaDefinition = {};

	public kind = "";

	public errors: string[] = [];

	public control?: ControlState;

	public constructor(
		$scope: angular.IScope
	) {
		this.inputId = `json-schema-control-${$scope.$id}`;
	}

	public $onChanges(): void {
		this.basePath = this.basePath ?? "";
		this.parentEnabled = this.parentEnabled ?? true;
		this.refresh();
	}

	public refresh(): void {
		if(!this.session || !this.ui) {
			return;
		}

		const runtime = this.session.runtime;
		const state = this.visibilityState();
		this.visible = state.visible;
		this.enabled = state.enabled;
		this.kind = this.ui.type;

		if(this.ui.type !== "Control") {
			return;
		}

		this.path = controlPath(this.ui.scope ?? "#", this.basePath);
		this.schema = schemaAtScope(runtime.schema, this.ui.scope ?? "#", this.context);
		this.enabled = this.enabled && !this.schema.readOnly;
		this.kind = this.controlKind();

		this.errors = this.session.showErrors ? errorsAtPath(runtime.errors, this.path) : [];

		const scope = this.ui.scope ?? "#";
		const propertyIndex = scope.lastIndexOf("/properties/");
		const parent = propertyIndex < 0 ? undefined : schemaAtScope(runtime.schema, scope.slice(0, propertyIndex), this.context);
		const property = propertyIndex < 0 ? "" : scope.slice(propertyIndex + "/properties/".length).replace(/~1/g, "/")
			.replace(/~0/g, "~");
		const descriptions = [
			this.schema.description ? `${this.inputId}-description` : "",
			this.errors.length ? `${this.inputId}-errors` : ""
		].filter(Boolean);

		this.control = {
			session: this.session,
			ui: this.ui,
			schema: this.schema,
			path: this.path,
			contextPath: this.path.split(".").slice(0, -1)
				.join("."),
			title: this.controlTitle(),
			id: this.inputId,
			required: Boolean(parent?.required?.includes(property)),
			invalid: this.errors.length > 0,
			describedBy: descriptions.join(" ") || undefined,
			value: dataAtPath(runtime.data, this.path),
			enabled: this.enabled,
			isVisible: () => this.visibilityState().visible,
			canEdit: () => this.canEdit(),
			change: value => this.change(value)
		};
	}

	public change(value: unknown): void {
		if(this.canEdit()) {
			this.session.update(this.path, value);
		}
	}

	public setEntityTimeZone(value?: string): void {
		if(!this.control) {
			return;
		}

		const runtime = this.session.runtime;
		const currentValue = dataAtPath(runtime.data, this.control.path);

		if(!runtime.isVisible(this.control.path) || currentValue == null) {
			value = undefined;
		} else if(currentValue !== this.control.value) {
			return;
		}

		if(this.session.timeZones.set(this.control.contextPath, value, this.control.path)) {
			this.session.revision++;
		}
	}

	private canEdit(): boolean {
		const state = this.visibilityState();

		return state.visible && state.enabled && !this.schema.readOnly;
	}

	private visibilityState(): ReturnType<typeof elementState> {
		const runtime = this.session.runtime;

		return elementState(
			this.ui,
			runtime.data,
			this.basePath,
			runtime.ajv,
			true,
			this.parentEnabled,
			this.session.readonly
		);
	}

	private controlKind(): string {
		if(enumOptions(this.schema).length) {
			return "enum";
		}

		if(this.session.runtime.getAlternatives(this.schema, this.path)) {
			return "alternative";
		}

		const format = this.schema.format;
		const formats = ["entity", "datapoint", "base64", "file", "date", "date-time"];
		const nullOnly = this.schema.type === "null" || Array.isArray(this.schema.type) && this.schema.type.every(type => type === "null");
		const type = schemaType(this.schema) ?? (nullOnly ? "null" : undefined);
		const stringFormat = ["base64", "file", "date", "date-time"].includes(format ?? "");
		const numericFormat = ["entity", "datapoint"].includes(format ?? "");
		const supportsFormat = stringFormat ? type === "string" : numericFormat ? type === "number" || type === "integer" : false;

		return format && formats.includes(format) && supportsFormat ? format : type ?? "unsupported";
	}

	private controlTitle(): string {
		const label = createLabelDescriptionFrom({
			...this.ui,
			type: "Control",
			scope: this.ui.scope ?? "#"
		}, this.schema);

		return label.show ? label.text : "";
	}
}

export default angular.module("vesl.extensions.json-schema.node", [
	arrayControl,
	alternativeControl,
	booleanControl,
	dateControl,
	datetimeControl,
	enumControl,
	fileControl,
	layout,
	label,
	markdown,
	numberControl,
	nullControl,
	objectControl,
	reference,
	stringControl
])
	.component("jsonSchemaNode", {
		template,
		controller: NodeController,
		bindings: {
			session: "<",
			ui: "<",
			context: "<",
			basePath: "<",
			parentEnabled: "<",
			revision: "<"
		}
	})
	.name;
